// src/components/Auth/ResetPassword.jsx

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  confirmPasswordReset,
  getAuth,
  verifyPasswordResetCode,
} from "firebase/auth";
import {
  Loader2,
  CheckCircle,
  XCircle,
  KeyRound,
  Eye,
  EyeOff,
} from "lucide-react";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = getAuth();

  const oobCode = searchParams.get("oobCode") || "";

  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("checking"); // checking | ready | success | error
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [pwd1, setPwd1] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);

  useEffect(() => {
    const check = async () => {
      if (!oobCode) {
        setStatus("error");
        setMessage("Missing reset code.");
        return;
      }
      try {
        const em = await verifyPasswordResetCode(auth, oobCode);
        setEmail(em);
        setStatus("ready");
      } catch (e) {
        console.error(e);
        setStatus("error");
        switch (e.code) {
          case "auth/expired-action-code":
            setMessage("This link has expired. Please request a new one.");
            break;
          case "auth/invalid-action-code":
            setMessage("This link is invalid. Please request a new one.");
            break;
          default:
            setMessage("Unable to validate link. Please try again.");
        }
      }
    };
    check();
  }, [auth, oobCode]);

  const validate = () => {
    if (!pwd1 || !pwd2) return "Enter and confirm your new password.";
    if (pwd1 !== pwd2) return "Passwords do not match.";
    if (pwd1.length < 8) return "Password must be at least 8 characters.";
    // Optional: add stronger checks if you want (numbers/symbols)
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    try {
      setLoading(true);
      await confirmPasswordReset(auth, oobCode, pwd1);
      setStatus("success");
      setMessage("Your password has been reset successfully.");
      setTimeout(() => navigate("/login?reset_password=success"), 1500);
    } catch (e) {
      console.error(e);
      setStatus("error");
      switch (e.code) {
        case "auth/weak-password":
          setMessage("Password is too weak. Try a longer password.");
          break;
        case "auth/expired-action-code":
          setMessage("This link has expired. Please request a new one.");
          break;
        case "auth/invalid-action-code":
          setMessage("This link is invalid. Please request a new one.");
          break;
        default:
          setMessage("Could not reset password. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white p-8 rounded-2xl shadow-lg">
        <div className="flex items-center gap-2 mb-2">
          <KeyRound className="w-5 h-5 text-slate-700" />
          <h1 className="text-lg font-semibold text-slate-900">
            Reset Password
          </h1>
        </div>

        {status === "checking" && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
          </div>
        )}

        {status === "error" && (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto bg-red-50 rounded-full flex items-center justify-center mb-3">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <p className="text-sm text-slate-600 mb-6">{message}</p>
            <div className="space-y-2">
              <Link
                to="/login"
                className="block w-full text-center border border-slate-300 hover:bg-slate-50 px-4 py-2.5 rounded-lg text-sm font-medium"
              >
                Back to Login
              </Link>
            </div>
          </div>
        )}

        {status === "ready" && (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-sm text-slate-600">
              Resetting password for{" "}
              <span className="font-medium">{email}</span>
            </p>

            {/* New password */}
            <div className="relative">
              <label htmlFor="pwd1" className="sr-only">
                New Password
              </label>
              <input
                id="pwd1"
                type={show1 ? "text" : "password"}
                value={pwd1}
                onChange={(e) => setPwd1(e.target.value)}
                placeholder="New password (min 8 chars)"
                className="w-full pr-11 pl-4 py-3 rounded-lg border border-slate-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShow1((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
                aria-label={show1 ? "Hide password" : "Show password"}
              >
                {show1 ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>

            {/* Confirm password */}
            <div className="relative">
              <label htmlFor="pwd2" className="sr-only">
                Confirm Password
              </label>
              <input
                id="pwd2"
                type={show2 ? "text" : "password"}
                value={pwd2}
                onChange={(e) => setPwd2(e.target.value)}
                placeholder="Confirm new password"
                className="w-full pr-11 pl-4 py-3 rounded-lg border border-slate-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShow2((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
                aria-label={show2 ? "Hide password" : "Show password"}
              >
                {show2 ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary/90 text-white py-3 rounded-lg font-semibold transition-all disabled:opacity-50"
            >
              {loading ? "Resetting…" : "Reset Password"}
            </button>

            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-primary hover:underline"
              >
                Back to Login
              </Link>
            </div>
          </form>
        )}

        {status === "success" && (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto bg-green-50 rounded-full flex items-center justify-center mb-3">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <p className="text-sm text-slate-700 mb-4">{message}</p>
            <button
              onClick={() => navigate("/login?reset_password=success")}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-all"
            >
              Continue
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
